/**
 * PersistedEventTogglesSlot — Per-performance event assignment toggles for
 * the persisted (My Rodeos) Edit Performance modal.
 *
 * Renders a multi-select toggle list of every event on the parent rodeo,
 * with a master "Toggle all" Switch at the top. Each toggle's checked
 * state reflects whether a `performance_events` row exists linking the
 * given event to this performance. Flipping a toggle creates or deletes
 * that join row directly via the generated `performance_events` API
 * client — there is no Save button here; changes apply immediately.
 *
 * Why immediate (not deferred) save:
 *   - The surrounding PerformanceDialog's Save Changes button persists
 *     fields on the `performances` row only. Wiring a deferred apply for
 *     join rows through the dialog would require new plumbing on a shared
 *     component used by the wizard too. Immediate per-toggle save keeps
 *     state simple and matches the previous behaviour of the inline event
 *     edit/delete buttons in this same slot.
 *   - "Unassign" is non-destructive: it removes the join row but leaves
 *     the underlying `events` row (and its rounds) intact.
 *
 * Master toggle semantics: tri-state — clicking flips to all-on when any
 * event is off; otherwise all-off. The visual ring on the master Switch
 * communicates "mixed".
 */
import { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useRodeoEvents } from '@/api/extensions/useRodeoEvents';
import { useEventsByPerformance } from '@/hooks/useEventsByPerformance';
import {
  usePerformanceEventsClient,
  performanceEventsKeys,
} from '@/api/generated_hooks/generated-performance_events-hooks';
import { getEventDisplayTitle } from '@/utils/eventTypes';

export interface PersistedEventTogglesSlotProps {
  /** The performance whose join rows we're editing. */
  performanceId: string;
  /** The parent rodeo — used to fetch the full event list. */
  rodeoId: string;
}

export function PersistedEventTogglesSlot({
  performanceId,
  rodeoId,
}: PersistedEventTogglesSlotProps) {
  const queryClient = useQueryClient();
  const client = usePerformanceEventsClient();

  // All events on the rodeo (both assigned-here and not).
  const { data: allEvents, isLoading: eventsLoading } = useRodeoEvents(rodeoId);
  // Existing join rows for this performance — drive each toggle's checked state.
  const { data: joinRows, isLoading: joinLoading } = useEventsByPerformance(
    performanceId,
  );

  // Per-event in-flight set so we can spinner+disable individual toggles
  // without freezing the rest of the list.
  const [pending, setPending] = useState<Set<string>>(new Set());
  // Surface the most recent failure inline — the dialog has no toast hook.
  const [error, setError] = useState<string | null>(null);

  // Build the display row for each event. Title resolution (via
  // `getEventDisplayTitle`) prefers the custom `event_name`; if that's blank
  // it falls back to the trigger-denormalised event-type label (e.g.
  // "Team Roping"). Only events that lack both end up showing the literal
  // "Unnamed event" fallback. The secondary detail line carries the fees and
  // prize so equally-named events stay distinguishable.
  const events = useMemo(
    () =>
      ((allEvents ?? []) as Array<Record<string, unknown>>).map((ev) => {
        const title = getEventDisplayTitle(
          ev as Parameters<typeof getEventDisplayTitle>[0],
          'Unnamed event',
        );
        const entryFee =
          typeof ev.entry_fee === 'number' ? (ev.entry_fee as number) : null;
        const txnFee =
          typeof ev.transaction_fee === 'number'
            ? (ev.transaction_fee as number)
            : null;
        const prizeTotal =
          typeof ev.total_prize_amount === 'number'
            ? (ev.total_prize_amount as number)
            : null;
        const meta =
          (ev.metadata as Record<string, unknown> | null | undefined) ?? {};
        const category =
          typeof meta.event_category === 'string'
            ? (meta.event_category as string)
            : null;
        const detailParts: string[] = [];
        if (entryFee != null) detailParts.push(`$${entryFee} entry`);
        if (txnFee != null && txnFee > 0) detailParts.push(`$${txnFee} txn`);
        if (prizeTotal != null && prizeTotal > 0) {
          detailParts.push(`$${prizeTotal} prize`);
        }
        return {
          id: ev.id as string,
          title,
          category,
          // Use a middle-dot separator to match the rest of the delegate UI.
          detail: detailParts.join(' · '),
        };
      }),
    [allEvents],
  );

  // Map event_id → join row id for quick lookup on disable/delete.
  const joinIdByEventId = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of joinRows ?? []) {
      const eid = (r as { event_id?: string | null }).event_id;
      const jid = (r as { id?: string | null }).id;
      if (eid && jid) map.set(eid, jid);
    }
    return map;
  }, [joinRows]);

  const items = useMemo(
    () =>
      events.map((ev) => ({
        id: ev.id,
        title: ev.title,
        category: ev.category,
        detail: ev.detail,
        assigned: joinIdByEventId.has(ev.id),
      })),
    [events, joinIdByEventId],
  );

  const allOn = items.length > 0 && items.every((i) => i.assigned);
  const allOff = items.every((i) => !i.assigned);
  const mixed = !allOn && !allOff;

  const isInitialLoading = eventsLoading || joinLoading;

  const setAssignment = async (eventId: string, assigned: boolean) => {
    const isAssigned = joinIdByEventId.has(eventId);
    if (isAssigned === assigned) return;

    setPending((prev) => new Set(prev).add(eventId));
    setError(null);
    try {
      if (assigned) {
        await client.create({
          event_id: eventId,
          performance_id: performanceId,
          // The join carries an optional per-perf max override; null means
          // "inherit the rodeo-level event cap".
          max_contestants: null,
        } as Parameters<typeof client.create>[0]);
      } else {
        const joinId = joinIdByEventId.get(eventId);
        if (joinId) {
          await client.delete(joinId);
        }
      }
      // The list of joins drives every other consumer (event count chips,
      // expanded perf rows). Invalidate broadly so all surfaces refresh.
      await queryClient.invalidateQueries({ queryKey: performanceEventsKeys.all });
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to update assignment. Please try again.';
      setError(message);
    } finally {
      setPending((prev) => {
        const next = new Set(prev);
        next.delete(eventId);
        return next;
      });
    }
  };

  const handleToggleAll = async () => {
    // Mixed and off both flip to "all on"; on flips to "all off".
    const target = !allOn;
    const changing = items.filter((i) => i.assigned !== target);
    if (changing.length === 0) return;

    // Mark every changing event as pending so the UI dims them all at once.
    setPending((prev) => {
      const next = new Set(prev);
      for (const item of changing) next.add(item.id);
      return next;
    });
    setError(null);

    try {
      await Promise.all(
        changing.map(async (item) => {
          if (target) {
            await client.create({
              event_id: item.id,
              performance_id: performanceId,
              max_contestants: null,
            } as Parameters<typeof client.create>[0]);
          } else {
            const joinId = joinIdByEventId.get(item.id);
            if (joinId) await client.delete(joinId);
          }
        }),
      );
      await queryClient.invalidateQueries({ queryKey: performanceEventsKeys.all });
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to update some assignments. Please review and retry.';
      setError(message);
    } finally {
      setPending((prev) => {
        const next = new Set(prev);
        for (const item of changing) next.delete(item.id);
        return next;
      });
    }
  };

  if (isInitialLoading) {
    return (
      <div className="space-y-2">
        <h3 className="text-sm font-semibold">Events in this Performance</h3>
        <p className="text-sm text-muted-foreground italic flex items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading events…
        </p>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="space-y-2">
        <h3 className="text-sm font-semibold">Events in this Performance</h3>
        <p className="text-sm text-muted-foreground italic">
          No events on this rodeo yet. Add events from the rodeo edit page first.
        </p>
      </div>
    );
  }

  const masterLabel = allOn
    ? 'All events enabled'
    : allOff
      ? 'No events enabled'
      : 'Some events enabled';
  const masterPending = pending.size > 1;

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold">Events in this Performance</h3>
        <p className="text-xs text-muted-foreground">
          Toggle which events run in this performance. Changes save automatically.
        </p>
      </div>

      {/* Master toggle */}
      <div className="flex items-center justify-between border rounded-md px-3 py-2 bg-muted/30">
        <Label
          htmlFor="persisted-event-toggles-master"
          className="font-medium text-sm cursor-pointer"
        >
          Toggle all
          <span className="block text-xs font-normal text-muted-foreground">
            {masterLabel}
          </span>
        </Label>
        <div className="flex items-center gap-2">
          {masterPending && (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
          )}
          <Switch
            id="persisted-event-toggles-master"
            checked={allOn}
            onCheckedChange={handleToggleAll}
            disabled={masterPending}
            aria-label="Toggle all events"
            className={mixed ? 'ring-2 ring-amber-400/60' : ''}
          />
        </div>
      </div>

      {/* Per-event toggles */}
      <div className="space-y-1.5">
        {items.map((item) => {
          const id = `persisted-event-toggle-${item.id}`;
          const itemPending = pending.has(item.id);
          return (
            <div
              key={item.id}
              className="flex items-center justify-between gap-3 border rounded-md px-3 py-2 hover:bg-muted/40 transition-colors"
            >
              <Label
                htmlFor={id}
                className="flex-1 min-w-0 cursor-pointer"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium truncate">
                    {item.title}
                  </span>
                  {item.category && (
                    <span className="text-xs text-muted-foreground capitalize">
                      {item.category}
                    </span>
                  )}
                </div>
                {item.detail && (
                  <p className="text-xs text-muted-foreground truncate mt-0.5">
                    {item.detail}
                  </p>
                )}
              </Label>
              <div className="flex items-center gap-2">
                {itemPending && (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                )}
                <Switch
                  id={id}
                  checked={item.assigned}
                  onCheckedChange={(checked) => setAssignment(item.id, checked)}
                  disabled={itemPending}
                  aria-label={`Toggle ${item.title} for this performance`}
                />
              </div>
            </div>
          );
        })}
      </div>

      {error && (
        <p className="text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export default PersistedEventTogglesSlot;
